import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import {
  ProgressBarWrapper,
  ProgressBarBackground,
  ProgressBarRatio,
  ProgressBarLabel,
} from './ProgressBar.styles';

export interface ProgressBarProps {
  ratio: number;
  label?: string;
  backgroundColor?: string;
  foregroundColor?: string;
  foregroundFontColor?: string;
  backgroundFontColor?: string;
  customClassName?: string;
}

const ProgressBar = (props: ProgressBarProps): JSX.Element => {
  const {
    ratio,
    label,
    backgroundColor,
    foregroundColor,
    foregroundFontColor,
    backgroundFontColor,
    customClassName,
  } = props;

  const [labelOffset, setLabelOffset] = useState(0);
  const [labelFontColor, setLabelFontColor] = useState(foregroundFontColor);

  const roundedRatio = Math.round(ratio);

  let ratioValue = roundedRatio;

  // Preventing <0 and >100 % values
  if (roundedRatio > 100) {
    ratioValue = 100;
  } else if (roundedRatio < 0) {
    ratioValue = 0;
  }

  // Using Refs for calculating element width
  const progressBarRef: any = useRef();
  const ratioRef: any = useRef();
  const ratioLabelRef: any = useRef();

  const calculateLabelPositioning = () => {
    const progressTotalWidth = progressBarRef.current.offsetWidth;
    const progressWidth = ratioRef.current.offsetWidth;
    const labelWidth = ratioLabelRef.current.offsetWidth - labelOffset;

    if (labelWidth <= progressWidth) {
      const labelOffsetValue = (progressWidth - labelWidth) / 2;
      setLabelOffset(labelOffsetValue);
      setLabelFontColor(foregroundFontColor);
    } else if (progressTotalWidth - progressWidth > labelWidth + 8) {
      const labelOffsetValue = progressWidth + 8;
      setLabelOffset(labelOffsetValue);
      setLabelFontColor(backgroundFontColor);
    }
  };

  // This triggers every time there is a layout change as state vars don't update inside event handler
  useLayoutEffect(() => {
    window.addEventListener('resize', calculateLabelPositioning);
    return () =>
      window.removeEventListener('resize', calculateLabelPositioning);
  });

  // Needed for initial call
  useEffect(() => {
    calculateLabelPositioning();
  }, [ratioValue]);

  const progressBarBackgroundStyles = {
    backgroundColor,
  };

  const progressBarStyles = {
    backgroundColor: foregroundColor,
    width: `${ratioValue}%`,
  };

  const progressBarLabelStyles = {
    textIndent: `${labelOffset}px`,
    color: labelFontColor,
  };

  return (
    <ProgressBarWrapper className={customClassName}>
      <ProgressBarBackground
        ref={progressBarRef}
        style={progressBarBackgroundStyles}
      >
        <ProgressBarRatio style={progressBarStyles} ref={ratioRef} />
        <ProgressBarLabel ref={ratioLabelRef} style={progressBarLabelStyles}>
          {`${ratioValue}% ${label}`}
        </ProgressBarLabel>
      </ProgressBarBackground>
    </ProgressBarWrapper>
  );
};

ProgressBar.defaultProps = {
  label: '',
  backgroundColor: '#1835581a',
  foregroundColor: '#2F9E44',
  foregroundFontColor: '#fff',
  backgroundFontColor: '#2F9E44',
  customClassName: '',
};
export default ProgressBar;
